import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// Única importação de estilo em todo o app. O arquivo local encadeia o
// Tailwind, a varredura de fontes e a folha do design system — ver os
// comentários em index.css sobre por que a varredura vive aqui.
import './index.css';

import { App } from './App.tsx';

/* ==============================================================================
   Restaura a aparência ANTES do primeiro render
   ==============================================================================
   Tema, densidade e contraste eram salvos em `localStorage` e nunca lidos de
   volta — recarregar a página perdia a escolha silenciosamente. A tela de
   configuração escrevia, e nada restaurava.

   Por que aqui e não num `useEffect`
   ----------------------------------
   Um efeito roda DEPOIS do primeiro render. A página apareceria no tema escuro
   e trocaria para o claro num segundo quadro — o "flash of wrong theme", que é
   visível e desagradável justamente em quem escolheu o tema claro.

   Rodando no módulo, antes de `createRoot`, os atributos já estão no `<html>`
   quando o primeiro pixel é pintado.

   `try/catch` porque `localStorage` lança em modo privado de alguns
   navegadores, e perder a preferência é aceitável — não abrir o app não é.
============================================================================== */
function restoreAppearance(): void {
  try {
    const root = document.documentElement;

    const theme = localStorage.getItem('concord.theme');
    // `dark` é a AUSÊNCIA do atributo: o bloco `@theme` já é o tema escuro, e
    // `[data-theme='dark']` não existe no CSS. Escrevê-lo não faria nada.
    if (theme !== null && theme !== 'dark') root.dataset.theme = theme;

    const density = localStorage.getItem('concord.density');
    if (density !== null) root.dataset.density = density;

    const contrast = localStorage.getItem('concord.contrast');
    if (contrast !== null && contrast !== 'normal') root.dataset.contrast = contrast;
  } catch {
    // Sem acesso a localStorage: os padrões do CSS valem, e o app abre.
  }
}

restoreAppearance();

const container = document.getElementById('root');
if (!container) {
  // Falha explícita em vez de tela branca silenciosa: se o index.html mudar e
  // #root desaparecer, o erro precisa dizer isso.
  throw new Error('Elemento #root não encontrado em index.html');
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
